import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { Currency } from '@freelancer/datastore/collections';
import { isImageFile } from '@freelancer/ui';
import { FileDisplayType } from '@freelancer/ui/file-display';
import {
  IconBackdrop,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import {
  AspectRatioValue,
  PictureDisplay,
  PictureLayout,
  PictureLoadMethod,
  PictureMetadata,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { FontWeight, TextAlign, TextSize } from '@freelancer/ui/text';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ShowcaseCardVariant } from './showcase-card.types';

@Component({
  selector: 'fl-showcase-card-title',
  template: `
    <div class="Heading">
      <fl-text
        [size]="TextSize.SMALL"
        [weight]="FontWeight.MEDIUM"
        [maxLines]="maxLines"
        [flMarginRight]="titlePrice ? 'xxsmall' : 'none'"
      >
        <ng-content></ng-content>
      </fl-text>
      <p
        class="text-small Heading-price font-medium text-right"
        *ngIf="titlePrice"
      >
        {{ titlePrice | flCurrency: currency.code }}
      </p>
    </div>
  `,
  styleUrls: ['./showcase-card-title.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardTitleComponent {
  FontWeight = FontWeight;
  TextAlign = TextAlign;
  TextSize = TextSize;

  @Input() maxLines?: number;
  @Input() titlePrice?: number;
  @Input() currency: Currency;
}

@Component({
  selector: 'fl-showcase-card-description',
  template: `
    <p class="text-xsmall">
      <ng-content></ng-content>
    </p>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardDescriptionComponent {
  TextSize = TextSize;
}
@Component({
  selector: 'fl-showcase-card-expandable-content',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardExpandableContentComponent {}

@Component({
  selector: 'fl-showcase-card-carousel-item',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShowcaseCardCarouselItemComponent {
  @Input() src: string;
  @Input() alt: string;

  @ContentChild('imageCaption')
  imageCaption: TemplateRef<any>;
}

@Component({
  selector: 'fl-showcase-card',
  template: `
    <div
      class="CardContainer"
      [class.ScaleOnHover]="scaleOnHover"
      [class.ToggleBorderRadius]="showcaseCardExpandableContentComponent"
    >
      <div
        flTrackingSection="ShowcaseCardSection"
        class="ImageContainer"
      >
        <ng-container *ngIf="!link; else clickableImage">
          <ng-template [ngTemplateOutlet]="content"></ng-template>
        </ng-container>
        <ng-template #clickableImage>
          <fl-button
            flTrackingLabel="ShowcaseCard"
            [display]="'block'"
            [link]="link"
            [newTab]="newTab"
          >
            <ng-template [ngTemplateOutlet]="content"></ng-template>
          </fl-button>
        </ng-template>

        <ng-template #content>
          <ng-container
            *ngIf="(isImage && !error) || carousel; else defaultThumbnail"
          >
            <div
              *ngIf="showLikeAction || showIconAction"
              class="IconWrapper"
            >
              <fl-icon
                *ngIf="showLikeAction"
                i18n-label="Like button text"
                label="Like"
                [flMarginRight]="showIconAction ? 'xxsmall' : 'none'"
                [backdrop]="IconBackdrop.DARK"
                [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                [clickable]="true"
                [color]="IconColor.LIGHT"
                [name]="isLikeActive ? 'ui-heart-v2' : 'ui-heart-alt-v2'"
                (click)="handleLikeClick()"
              >
              </fl-icon>
              <fl-icon
                *ngIf="showIconAction"
                i18n-label="Action icon button text"
                [label]="labelIconAction"
                [backdrop]="IconBackdrop.DARK"
                [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                [clickable]="true"
                [color]="IconColor.LIGHT"
                [name]="iconActionName"
                (click)="handleActionClick()"
              >
              </fl-icon>
            </div>
            <ng-container *ngIf="carousel; else singleImage">
              <fl-carousel
                [currentIndex]="0"
                [showDotIndicator]="true"
              >
                <fl-carousel-prev-btn>
                  <fl-icon
                    class="Control"
                    [name]="'ui-arrow-left-alt'"
                    [size]="IconSize.SMALL"
                    [clickable]="true"
                    [backdrop]="IconBackdrop.WHITE"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                  ></fl-icon>
                </fl-carousel-prev-btn>
                <fl-carousel-item
                  *ngFor="
                    let item of carouselItems;
                    trackBy: trackByCarouselItem
                  "
                >
                  <fl-picture
                    class="Picture"
                    [alignCenter]="true"
                    [alt]="item.alt"
                    [borderRadiusTop]="scaleOnHover"
                    [borderRadiusBottom]="borderRadiusBottom"
                    [display]="PictureDisplay.BLOCK"
                    [fullWidth]="fullWidth"
                    [layout]="PictureLayout.INTRINSIC"
                    [loadMethod]="PictureLoadMethod.LAZY"
                    [objectFit]="PictureObjectFit.COVER"
                    [pictureMetadata]="pictureMetadata"
                    [fixedAspectRatio]="true"
                    [src]="item.src"
                    [width]="imageIntrinsicWidth"
                  ></fl-picture>
                </fl-carousel-item>
                <fl-carousel-next-btn>
                  <fl-icon
                    class="Control"
                    [name]="'ui-arrow-right-alt'"
                    [size]="IconSize.SMALL"
                    [backdrop]="IconBackdrop.WHITE"
                    [backdropStyle]="IconBackdropStyle.TRANSPARENT"
                    [clickable]="true"
                  ></fl-icon>
                </fl-carousel-next-btn>
              </fl-carousel>
            </ng-container>
            <ng-template #singleImage>
              <fl-picture
                [alignCenter]="true"
                [alt]="fileAlt"
                [borderRadiusTop]="scaleOnHover"
                [borderRadiusBottom]="borderRadiusBottom"
                [display]="PictureDisplay.BLOCK"
                [fixedAspectRatio]="
                  !aspectRatioValue
                    ? variant === ShowcaseCardVariant.LANDING_PAGE
                    : false
                "
                [fullWidth]="fullWidth"
                [layout]="PictureLayout.INTRINSIC"
                [loadMethod]="PictureLoadMethod.LAZY"
                [aspectRatioValue]="aspectRatioValue"
                [class.ImageContainer-showcaseVariant]="
                  variant === ShowcaseCardVariant.SHOWCASE_PAGE
                "
                [objectFit]="PictureObjectFit.COVER"
                [pictureMetadata]="pictureMetadata"
                [src]="fileSrc"
                [width]="imageIntrinsicWidth"
                (imageLoaded)="imageLoaded = true"
                (onError)="error = true"
              ></fl-picture>
            </ng-template>
          </ng-container>
          <fl-loading-text
            *ngIf="!error && !imageLoaded && isImage && !carousel"
            class="ImageContainer-loading"
            [padded]="false"
          ></fl-loading-text>
        </ng-template>

        <div
          *ngIf="variant === ShowcaseCardVariant.SHOWCASE_PAGE"
          [class]="{ 'ImageContainer-hoverItems': !keepHoverStateShown }"
          [flShowDesktop]="true"
        >
          <ng-content select="fl-showcase-card-hover-content"></ng-content>
        </div>

        <ng-template #defaultThumbnail>
          <div class="DefaultTemplate">
            <fl-file-display
              class="DefaultTemplate-icon"
              [src]="fileSrc"
              [iconSize]="IconSize.XXLARGE"
              [iconColor]="IconColor.LIGHT"
              [type]="FileDisplayType.ICON"
            ></fl-file-display>
          </div>
        </ng-template>
      </div>
      <div class="ShowcaseTitle">
        <fl-link
          *ngIf="link; else title"
          [link]="link"
          [newTab]="newTab"
          [underline]="LinkUnderline.NEVER"
        >
          <ng-template [ngTemplateOutlet]="title"></ng-template>
        </fl-link>

        <ng-template #title>
          <ng-content select="fl-showcase-card-title"></ng-content>
        </ng-template>

        <ng-content
          *ngIf="variant === ShowcaseCardVariant.SHOWCASE_PAGE"
          select="fl-showcase-likes"
        ></ng-content>
      </div>
      <ng-container *ngIf="variant === ShowcaseCardVariant.LANDING_PAGE">
        <div
          *ngIf="showcaseCardDescriptionComponent"
          class="Description"
        >
          <ng-content select="fl-showcase-card-description"></ng-content>
        </div>
      </ng-container>
      <div
        *ngIf="showcaseCardExpandableContentComponent"
        class="ContentWrapper"
      >
        <div class="Content">
          <ng-content select="fl-showcase-card-expandable-content"></ng-content>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./showcase-card.component.scss'],
})
export class ShowcaseCardComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  FileDisplayType = FileDisplayType;
  IconColor = IconColor;
  IconSize = IconSize;
  IconBackdropStyle = IconBackdropStyle;
  IconBackdrop = IconBackdrop;
  LinkUnderline = LinkUnderline;
  PictureDisplay = PictureDisplay;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;
  ShowcaseCardVariant = ShowcaseCardVariant;

  imageLoaded = false;
  error = false;
  isImage = true;
  isLikeActive = false;

  @Input() fullWidth = true;
  @Input() titlePrice: number;
  @Input() fileAlt: string;
  @Input() fileSrc: string;
  @Input() link?: string;
  @Input() newTab = false;
  @Input() keepHoverStateShown = false;
  @Input() pictureMetadata?: PictureMetadata;
  @Input() imageIntrinsicWidth?: number;
  @Input() borderRadiusBottom = false;

  /**
   * Set the aspect ratio of the image on the showcase card
   */
  @Input() aspectRatioValue?: AspectRatioValue;

  /**
   * Shows action icons on the top-right side of the card
   */
  @Input() showLikeAction = false;
  @Input() showIconAction = false;
  @Input() iconActionName: string;
  @Input() labelIconAction: string;

  @Input() scaleOnHover = false;
  @Input() carousel = false;

  @Input() variant: ShowcaseCardVariant = ShowcaseCardVariant.LANDING_PAGE;
  /**
   * Set the height of the image for showcase variant in order to keep
   * the masonry style on showcase work
   */
  @HostBinding('style.--image-desktop-xxxxlarge-height')
  @Input()
  imageXXXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xxxlarge-height')
  @Input()
  imageXXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xxlarge-height')
  @Input()
  imageXXLargeHeight?: string;

  @HostBinding('style.--image-desktop-xlarge-height')
  @Input()
  imageXLargeHeight?: string;

  @HostBinding('style.--image-desktop-large-height')
  @Input()
  imageLargeHeight?: string;

  @HostBinding('style.--image-desktop-height')
  @Input()
  imageHeight?: string;

  @HostBinding('style.--image-desktop-small-height')
  @Input()
  imageDesktopSmallHeight?: string;

  @HostBinding('style.--image-tablet-height')
  @Input()
  imageTabletHeight?: string;

  @HostBinding('style.--image-mobile-large-height')
  @Input()
  imageMobileLargeHeight?: string;

  @HostBinding('style.--image-mobile-height')
  @Input()
  imageMobileHeight = '100%';

  @Output() onClickLike = new EventEmitter<void>();
  @Output() onClickAction = new EventEmitter<void>();

  @ContentChild(ShowcaseCardExpandableContentComponent)
  showcaseCardExpandableContentComponent: ShowcaseCardExpandableContentComponent;

  @ContentChild(ShowcaseCardDescriptionComponent)
  showcaseCardDescriptionComponent: ShowcaseCardDescriptionComponent;

  @ContentChildren(ShowcaseCardCarouselItemComponent)
  carouselItems: QueryList<ShowcaseCardCarouselItemComponent>;

  // FIXME: T300116 Enable hydration. Currently fails on client side with
  // TypeError: Cannot read properties of null (reading 'firstChild')
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  private _indexSubject$ = new BehaviorSubject(0);
  index$ = this._indexSubject$.asObservable();

  private subscriptions = new Subscription();

  ngAfterViewInit(): void {
    // Reset the index to 0 when number of carousel items has changed
    this.subscriptions.add(
      this.carouselItems.changes.subscribe(item => {
        this._indexSubject$.next(0);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('fileSrc' in changes) {
      this.isImage = isImageFile(this.fileSrc);
    }
  }

  handleLikeClick(): void {
    this.isLikeActive = !this.isLikeActive;
    this.onClickLike.emit();
  }

  handleActionClick(): void {
    this.onClickAction.emit();
  }

  trackByCarouselItem(
    _: number,
    item: ShowcaseCardCarouselItemComponent,
  ): string {
    return `${item.src}:${item.alt}`;
  }
}
